import { useState, useEffect, useRef, useCallback } from "react";
import { DealerLayout } from "@/components/shared/DealerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { uploadMyDealerDocument, getBusinessPlanDraft, saveBusinessPlanDraft } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Download, Save, Loader2, Check, Plus, Trash2, Briefcase, UserCircle, Users, Cloud, CloudOff } from "lucide-react";
import jsPDF from "jspdf";

const STEPS = [
  { key: "overview", title: "1. Business Overview" },
  { key: "org", title: "2. Organizational Structure" },
  { key: "markets", title: "3. Markets & Growth" },
  { key: "financials", title: "4. Financial Plan" },
  { key: "support", title: "5. Store Support Plan" },
];

const emptyPerson = () => ({ id: Date.now() + Math.random(), name: "", title: "" });
const emptyPartner = () => ({ id: Date.now() + Math.random(), name: "", role: "", status: "Active" });
const emptyOtherBiz = () => ({ id: Date.now() + Math.random(), name: "", description: "" });
const emptyMarket = () => ({ id: Date.now() + Math.random(), market: "", stores: "", type: "New" });

const DEFAULT = {
  dealer_name: "",
  overview: {
    business_name: "",
    dba: "",
    hq_location: "",
    owner_principal: "",
    partners: [emptyPartner()],
    professional_experience: "",
    other_businesses: [emptyOtherBiz()],
    previous_carriers: "",
  },
  org: {
    owner: { name: "", title: "Owner / Principal", phone: "", email: "" },
    sales_team: [emptyPerson()],
    operations_team: [emptyPerson()],
    other_team: [emptyPerson()],
    sales_associate_count: 2,
  },
  markets: {
    home_market: "",
    first_store_location: "",
    additional_markets: "",
    growth_0_6: [emptyMarket()],
    growth_6_12: [emptyMarket()],
    growth_1_2: [emptyMarket()],
    growth_2plus: [emptyMarket()],
    growth_strategy: "",
    total_investment: {
      owner_equity: "",
      debt_loans: "",
      outside_investment: "",
      working_capital: "",
      reserve: "",
      notes: "",
    },
  },
  financials: {
    capex: {
      buildout: "",
      equipment: "",
      inventory: "",
      grand_opening: "",
      contingency: "",
    },
    opex: {
      rent: "",
      insurance: "",
      utilities: "",
      software: "",
      staffing_payroll: "",
      ongoing_marketing: "",
      overhead: "",
    },
    financial_plan_notes: "",
  },
  support: {
    staffing_strategy: "",
    onboarding_training: "",
    grand_opening_marketing: { tactics: "", budget: "" },
    ongoing_marketing: { tactics: "", budget: "" },
    frontline_comp_plan: "",
  },
};

function StepIndicator({ currentStep, setCurrentStep }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2 mb-6">
      {STEPS.map((step, i) => {
        const isActive = i === currentStep;
        const isDone = i < currentStep;
        return (
          <button
            key={step.key}
            onClick={() => setCurrentStep(i)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              isActive ? "bg-navy text-white" : isDone ? "bg-teal/10 text-teal" : "bg-slate-100 text-slate-400 hover:bg-slate-200"
            }`}
            data-testid={`bpoc-step-${step.key}`}
          >
            {isDone ? <Check className="w-3.5 h-3.5" /> : <span className="w-4 h-4 rounded-full border flex items-center justify-center text-[10px]">{i + 1}</span>}
            <span className="hidden md:inline">{step.title.split(". ")[1]}</span>
          </button>
        );
      })}
    </div>
  );
}

function Money({ label, value, onChange, tid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-600">{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
        <Input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0"
          className="pl-7 text-sm"
          data-testid={tid}
        />
      </div>
    </div>
  );
}

const money = (v) => {
  if (v === "" || v == null) return "$0";
  const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
  if (isNaN(n)) return `$${v}`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

// Deep-merge a partial draft over DEFAULT so missing keys stay valid (arrays
// must exist, nested objects populated) even if an older/partial draft loads.
const mergeDraft = (base, patch) => {
  if (patch == null || typeof patch !== "object") return base;
  if (Array.isArray(base)) return Array.isArray(patch) && patch.length > 0 ? patch : base;
  if (typeof base !== "object") return patch !== undefined ? patch : base;
  const out = { ...base };
  for (const k of Object.keys(base)) {
    if (k in patch) out[k] = mergeDraft(base[k], patch[k]);
  }
  // Preserve any extra keys from the draft not in the base (forward-compat)
  for (const k of Object.keys(patch)) {
    if (!(k in out)) out[k] = patch[k];
  }
  return out;
};

// Migrate legacy org drafts (district_managers/store_managers) into the new
// Sales/Operations/Other lane structure. Runs once after mergeDraft.
const migrateOrg = (data) => {
  if (!data?.org) return data;
  const org = data.org;
  const hasNewLanes = Array.isArray(org.sales_team) && org.sales_team.some((p) => p?.name?.trim() || p?.title?.trim());
  if (hasNewLanes) return data;
  const dms = Array.isArray(org.district_managers) ? org.district_managers.filter((p) => p?.name?.trim() || p?.title?.trim()) : [];
  const sms = Array.isArray(org.store_managers) ? org.store_managers.filter((p) => p?.name?.trim() || p?.title?.trim()) : [];
  const sales = [...dms.map((p) => ({ ...p, title: p.title || "District Manager" })), ...sms.map((p) => ({ ...p, title: p.title || "Store Manager" }))];
  if (sales.length === 0) return data;
  return {
    ...data,
    org: {
      ...org,
      sales_team: sales.map((p) => ({ id: p.id || Date.now() + Math.random(), name: p.name || "", title: p.title || "" })),
    },
  };
};

export default function BusinessPlanOrgChartBuilder() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(DEFAULT);
  const [draftStatus, setDraftStatus] = useState("idle"); // idle | loading | saving | saved | error
  const [draftUpdatedAt, setDraftUpdatedAt] = useState(null);
  const hydratedRef = useRef(false);
  const debounceRef = useRef(null);

  // On mount: try to hydrate from saved draft
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDraftStatus("loading");
      try {
        const res = await getBusinessPlanDraft();
        if (cancelled) return;
        if (res?.data) {
          setFormData(migrateOrg(mergeDraft(DEFAULT, res.data)));
          setDraftUpdatedAt(res.updated_at || null);
          setDraftStatus("saved");
        } else {
          setDraftStatus("idle");
        }
      } catch {
        if (!cancelled) setDraftStatus("error");
      } finally {
        // Mark hydrated after the state has settled so we don't autosave the
        // just-hydrated data back to the server on first render.
        setTimeout(() => { if (!cancelled) hydratedRef.current = true; }, 200);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounced autosave: any formData change triggers a save 1.5s later
  const scheduleSave = useCallback((next) => {
    if (!hydratedRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setDraftStatus("saving");
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await saveBusinessPlanDraft(next);
        setDraftUpdatedAt(res?.updated_at || new Date().toISOString());
        setDraftStatus("saved");
      } catch {
        setDraftStatus("error");
      }
    }, 1500);
  }, []);

  // Watch formData → trigger scheduleSave
  useEffect(() => {
    scheduleSave(formData);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [formData, scheduleSave]);

  // ------- helpers -------
  const updateRoot = (field, value) => setFormData((p) => ({ ...p, [field]: value }));
  const updateSection = (section, field, value) =>
    setFormData((p) => ({ ...p, [section]: { ...p[section], [field]: value } }));
  const updateNested = (section, nested, field, value) =>
    setFormData((p) => ({
      ...p,
      [section]: { ...p[section], [nested]: { ...p[section][nested], [field]: value } },
    }));
  const updateListItem = (section, listKey, id, field, value) =>
    setFormData((p) => ({
      ...p,
      [section]: {
        ...p[section],
        [listKey]: p[section][listKey].map((it) => (it.id === id ? { ...it, [field]: value } : it)),
      },
    }));
  const addListItem = (section, listKey, factory) =>
    setFormData((p) => ({
      ...p,
      [section]: { ...p[section], [listKey]: [...p[section][listKey], factory()] },
    }));
  const removeListItem = (section, listKey, id) =>
    setFormData((p) => ({
      ...p,
      [section]: { ...p[section], [listKey]: p[section][listKey].filter((it) => it.id !== id) },
    }));

  // ------- PDF generation (5 slides, 16:9 landscape, PPTX-styled) -------
  const generatePDF = () => {
    // Page size matches PPTX 10 x 5.625 in => 254 x 142.875 mm (16:9 widescreen)
    const W = 254, H = 143;
    const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: [W, H] });
    const margin = 8;
    const dealerName = formData.dealer_name || "New Sub-Agent";
    const bizName = formData.overview.business_name || dealerName;

    // ---- Style constants (mirroring the sample deck) ----
    const BLACK = [0, 0, 0];
    const WHITE = [255, 255, 255];
    const GREY_TEXT = [30, 30, 30];
    const GREY_MUTED = [110, 110, 110];
    const GREY_ROW = [239, 239, 239];      // #EFEFEF alt-row fill
    const RED_TITLE = [227, 30, 36];       // Prime Media brand red for section titles
    const TEAL_LANE = [38, 211, 180];      // #26D3B4 lane headers (Sales/Ops/Other)
    const NAVY_OWNER = [30, 42, 71];       // #1E2A47 owner box fill
    const BORDER = [180, 180, 180];        // content-box thin border

    // Runs on every content slide (2-5). Cover has its own layout.
    const drawSlideChrome = (sectionTitle, pageNum) => {
      // Running header top-left: "[Business Name] | Business Plan" (sample style)
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.setTextColor(...BLACK);
      pdf.text(`${bizName} | Business Plan`, margin, 8);
      // Section title (large, RED, matches sample)
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(24);
      pdf.setTextColor(...RED_TITLE);
      pdf.text(sectionTitle, margin, 19);
      // Page indicator bottom-right
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(...GREY_MUTED);
      pdf.text(`Page ${pageNum}`, W - margin, H - 4, { align: "right" });
    };

    // Black header bar with white bold title
    const drawBlackHeaderBar = (x, y, w, title, subtitle) => {
      pdf.setFillColor(...BLACK);
      pdf.rect(x, y, w, 7, "F");
      pdf.setTextColor(...WHITE);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      const titleY = y + 4.9;
      if (subtitle) {
        // Measure title + subtitle to center them together as a group
        const titleW = pdf.getTextWidth(title);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        const subW = pdf.getTextWidth(subtitle);
        const totalW = titleW + 3 + subW;
        const startX = x + w / 2 - totalW / 2;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.text(title, startX, titleY);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.text(subtitle, startX + titleW + 3, titleY);
      } else {
        pdf.text(title, x + w / 2, titleY, { align: "center" });
      }
    };

    // Content box border under a header bar
    const drawContentBox = (x, y, w, h) => {
      pdf.setDrawColor(...BORDER);
      pdf.setLineWidth(0.3);
      pdf.setFillColor(...WHITE);
      pdf.rect(x, y, w, h, "S");
    };

    // Teal lane header for org chart columns
    const drawTealLane = (x, y, w, title) => {
      pdf.setFillColor(...TEAL_LANE);
      pdf.rect(x, y, w, 6, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(...BLACK);
      pdf.text(title, x + w / 2, y + 4.2, { align: "center" });
    };

    // Wrap text within width, returns lines drawn
    const drawWrapped = (text, x, y, w, opts = {}) => {
      const { size = 9, bold = false, color = GREY_TEXT, lh = 4 } = opts;
      pdf.setFont("helvetica", bold ? "bold" : "normal");
      pdf.setFontSize(size);
      pdf.setTextColor(...color);
      const lines = pdf.splitTextToSize(String(text || ""), w);
      lines.forEach((line, i) => pdf.text(line, x, y + i * lh));
      return lines.length * lh;
    };

    // Bulleted KV pipe row: "• Business Name | Meadowlark …"
    // Uses vector circle (filled for level-0, hollow for indented) instead of
    // unicode bullets to guarantee correct rendering with Helvetica encoding.
    const drawBulletDot = (cx, cy, indent) => {
      const r = 0.7;
      pdf.setDrawColor(...BLACK);
      pdf.setLineWidth(0.25);
      if (indent > 0) {
        pdf.setFillColor(...WHITE);
        pdf.circle(cx, cy, r, "FD");
      } else {
        pdf.setFillColor(...BLACK);
        pdf.circle(cx, cy, r, "F");
      }
    };
    const drawBulletKV = (x, y, w, k, v, indent = 0) => {
      drawBulletDot(x + indent + 1, y - 1.3, indent);
      const labelX = x + indent + 4;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...BLACK);
      const label = `${k} |`;
      pdf.text(label, labelX, y);
      const labelW = pdf.getTextWidth(label);
      pdf.setTextColor(...GREY_TEXT);
      const val = String(v || "").trim();
      if (val) {
        const lines = pdf.splitTextToSize(val, w - labelW - 4 - indent);
        lines.forEach((line, i) => pdf.text(line, labelX + labelW + 2, y + i * 4));
        return Math.max(5, lines.length * 4 + 1);
      }
      return 5;
    };

    // Simple bullet line
    const drawBullet = (x, y, w, text, indent = 0) => {
      drawBulletDot(x + indent + 1, y - 1.3, indent);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(...GREY_TEXT);
      const lines = pdf.splitTextToSize(String(text || ""), w - 4 - indent);
      lines.forEach((line, i) => pdf.text(line, x + indent + 4, y + i * 4));
      return Math.max(5, lines.length * 4 + 1);
    };

    // ============================================================
    // SLIDE 1 — COVER
    // ============================================================
    pdf.setFillColor(...WHITE);
    pdf.rect(0, 0, W, H, "F");
    // Top black bar + red thin accent
    pdf.setFillColor(...BLACK);
    pdf.rect(0, 0, W, 3, "F");
    pdf.setFillColor(...RED_TITLE);
    pdf.rect(0, 3, W, 1.2, "F");
    // Small brand mark top-left
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(...BLACK);
    pdf.text("PRIME MEDIA WIRELESS", margin, 14);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...GREY_MUTED);
    pdf.text("Master Agent | Total Wireless", margin, 18.5);

    // Main title (centered)
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(34);
    pdf.setTextColor(...BLACK);
    const titleLines = pdf.splitTextToSize(`${dealerName}`, W - margin * 4);
    let ty = 62 - (titleLines.length - 1) * 6;
    titleLines.forEach((l) => { pdf.text(l, W / 2, ty, { align: "center" }); ty += 12; });
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.setTextColor(...RED_TITLE);
    pdf.text("Business Plan", W / 2, ty + 2, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    pdf.setTextColor(...GREY_MUTED);
    pdf.text("New Total Wireless Sub-Agent", W / 2, ty + 12, { align: "center" });

    pdf.setFontSize(9);
    pdf.setTextColor(...GREY_MUTED);
    pdf.text(`Prepared for: Prime Media Wireless`, W / 2, H - 18, { align: "center" });
    pdf.text(new Date().toLocaleDateString(), W / 2, H - 12, { align: "center" });
    pdf.setFillColor(...BLACK);
    pdf.rect(0, H - 3, W, 3, "F");

    // ============================================================
    // SLIDE 2 — DEALER PROFILE
    // Left: Business Overview + Professional Experience (bordered boxes)
    // Right: Organizational Structure (Owner → Sales/Ops/Other lanes)
    // ============================================================
    pdf.addPage([W, H], "landscape");
    drawSlideChrome("Dealer Profile", 2);

    const colW = (W - margin * 2 - 6) / 2;
    const leftX = margin;
    const rightX = margin + colW + 6;
    const topY = 24;

    // ---- LEFT COLUMN ----
    const o = formData.overview;
    // Business Overview
    const overviewBoxH = 55;
    drawBlackHeaderBar(leftX, topY, colW, "Business Overview");
    drawContentBox(leftX, topY + 7, colW, overviewBoxH);
    let lY = topY + 12;
    const padX = leftX + 4;
    const rowW = colW - 8;
    lY += drawBulletKV(padX, lY, rowW, "Business Name", o.business_name);
    lY += drawBulletKV(padX, lY, rowW, "DBA (if applicable)", o.dba, 5);
    lY += drawBulletKV(padX, lY, rowW, "HQ Location", o.hq_location, 5);
    lY += drawBulletKV(padX, lY, rowW, "Owner Principal Name", o.owner_principal);
    // Business Partners
    const activePartners = o.partners.filter((p) => p.name.trim());
    if (activePartners.length > 0) {
      lY += drawBullet(padX, lY, rowW, "Business Partners (if applicable)");
      activePartners.forEach((p) => {
        lY += drawBullet(padX, lY, rowW, `${p.name}, ${p.role || "Partner"}${p.status ? ", " + p.status : ""}`, 5);
      });
    } else {
      lY += drawBullet(padX, lY, rowW, "Business Partners (if applicable) — none");
    }
    lY += drawBullet(padX, lY, rowW, "Master Agent | Prime Media");

    // Professional Experience (fills remaining left column)
    const peY = topY + 7 + overviewBoxH + 4;
    const peBoxH = H - peY - 10;
    drawBlackHeaderBar(leftX, peY, colW, "Professional Experience/Background");
    drawContentBox(leftX, peY + 7, colW, peBoxH);
    let peCurY = peY + 12;
    const experienceLines = String(o.professional_experience || "").split("\n").filter((l) => l.trim());
    if (experienceLines.length > 0) {
      experienceLines.forEach((line) => {
        peCurY += drawBullet(padX, peCurY, rowW, line);
      });
    }
    // Append other businesses as more bullets if user filled them
    const activeBiz = o.other_businesses.filter((b) => b.name.trim());
    activeBiz.forEach((b) => {
      peCurY += drawBullet(padX, peCurY, rowW, `${b.name}${b.description ? " | " + b.description : ""}`);
    });
    if (o.previous_carriers) {
      peCurY += drawBullet(padX, peCurY, rowW, `Previous Carriers | ${o.previous_carriers}`);
    }

    // ---- RIGHT COLUMN — Organizational Structure ----
    drawBlackHeaderBar(rightX, topY, colW, "Organizational Structure");
    const orgBoxY = topY + 7;
    const orgBoxH = H - orgBoxY - 10;
    drawContentBox(rightX, orgBoxY, colW, orgBoxH);

    // Owner box (centered near top, navy filled)
    const orgCenter = rightX + colW / 2;
    const ownerW = 62, ownerH = 15;
    const ownerX = orgCenter - ownerW / 2;
    const ownerY = orgBoxY + 6;
    pdf.setFillColor(...NAVY_OWNER);
    pdf.rect(ownerX, ownerY, ownerW, ownerH, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(...WHITE);
    const ownerName = formData.org.owner.name || formData.overview.owner_principal || "TBD";
    pdf.text(ownerName, orgCenter, ownerY + 6.5, { align: "center" });
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(9);
    pdf.setTextColor(...WHITE);
    pdf.text(formData.org.owner.title || "Owner", orgCenter, ownerY + 12, { align: "center" });

    // Connector line down to lane row
    const laneY = ownerY + ownerH + 6;
    pdf.setDrawColor(...BLACK);
    pdf.setLineWidth(0.35);
    pdf.line(orgCenter, ownerY + ownerH, orgCenter, laneY);

    // Three lanes: Sales | Operations | Other
    const laneGap = 3;
    const innerBoxW = colW - 8;
    const laneW = (innerBoxW - laneGap * 2) / 3;
    const lanesStartX = rightX + 4;
    const laneXs = [
      lanesStartX,
      lanesStartX + laneW + laneGap,
      lanesStartX + (laneW + laneGap) * 2,
    ];
    // Horizontal bus line connecting owner to each lane top
    const busY = laneY;
    pdf.line(laneXs[0] + laneW / 2, busY, laneXs[2] + laneW / 2, busY);
    // vertical drops to each lane
    laneXs.forEach((lx) => pdf.line(lx + laneW / 2, busY, lx + laneW / 2, busY + 3));

    const laneHeaderY = busY + 3;
    drawTealLane(laneXs[0], laneHeaderY, laneW, "Sales");
    drawTealLane(laneXs[1], laneHeaderY, laneW, "Operations");
    drawTealLane(laneXs[2], laneHeaderY, laneW, "Other");

    // Render members under each lane
    const memberH = 11;
    const memberGap = 1.5;
    const drawMember = (lx, my, name, title, alt) => {
      pdf.setDrawColor(...BORDER);
      pdf.setLineWidth(0.25);
      pdf.setFillColor(...(alt ? GREY_ROW : WHITE));
      pdf.rect(lx, my, laneW, memberH, "FD");
      // Truncate helper
      const fit = (t, maxW, size) => {
        pdf.setFontSize(size);
        let s = String(t || "");
        while (pdf.getTextWidth(s) > maxW - 2 && s.length > 3) s = s.slice(0, -1);
        if (s.length !== String(t || "").length) s = s.slice(0, -1) + "…";
        return s;
      };
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8.5);
      pdf.setTextColor(...BLACK);
      pdf.text(fit(name || "—", laneW, 8.5), lx + laneW / 2, my + 4.5, { align: "center" });
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(7.5);
      pdf.setTextColor(...GREY_MUTED);
      pdf.text(fit(title || "", laneW, 7.5), lx + laneW / 2, my + 9, { align: "center" });
    };

    const renderLane = (lx, list, appendSales) => {
      const filtered = list.filter((p) => (p?.name || "").trim() || (p?.title || "").trim());
      let my = laneHeaderY + 6 + memberGap;
      filtered.forEach((p, i) => {
        drawMember(lx, my, p.name || "—", p.title || "", i % 2 === 1);
        my += memberH + memberGap;
      });
      if (appendSales && formData.org.sales_associate_count > 0) {
        drawMember(
          lx,
          my,
          `Sales Associates (${formData.org.sales_associate_count})`,
          formData.org.sales_associate_count === 1 ? "Employee" : "Employees",
          filtered.length % 2 === 1
        );
      }
    };

    renderLane(laneXs[0], formData.org.sales_team, true);
    renderLane(laneXs[1], formData.org.operations_team, false);
    renderLane(laneXs[2], formData.org.other_team, false);

    // ============================================================
    // SLIDE 3 — GROWTH STRATEGY
    // ============================================================
    pdf.addPage([W, H], "landscape");
    drawSlideChrome("Growth Strategy", 3);

    // Left: Store Growth Commitments
    const growthBoxH = H - topY - 17;
    drawBlackHeaderBar(leftX, topY, colW, "Store Growth Commitments");
    drawContentBox(leftX, topY + 7, colW, growthBoxH);
    let gY = topY + 12;
    const drawGrowthBucket = (label, list) => {
      const filtered = list.filter((m) => m.market.trim());
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(...BLACK);
      pdf.text(label, leftX + 4, gY);
      gY += 4.5;
      if (filtered.length === 0) {
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(9);
        pdf.setTextColor(...GREY_MUTED);
        pdf.text("—", leftX + 8, gY);
        gY += 4.5;
      } else {
        filtered.forEach((m) => {
          const line = `Total Wireless | ${m.stores || 1} store(s) | ${m.type || "New"} — ${m.market}`;
          gY += drawBullet(leftX + 4, gY, colW - 8, line);
        });
      }
      gY += 2;
    };
    drawGrowthBucket("0 – 6 Months", formData.markets.growth_0_6);
    drawGrowthBucket("6 – 12 Months", formData.markets.growth_6_12);
    drawGrowthBucket("1 – 2 Years", formData.markets.growth_1_2);
    drawGrowthBucket("2+ Years", formData.markets.growth_2plus);
    if (formData.markets.growth_strategy) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(...BLACK);
      pdf.text("Growth Strategy Notes |", leftX + 4, gY);
      gY += 4;
      drawWrapped(formData.markets.growth_strategy, leftX + 8, gY, colW - 12, { size: 8.5, lh: 3.6 });
    }

    // Right top: Markets of Interest
    const rightTopH = 55;
    drawBlackHeaderBar(rightX, topY, colW, "Markets of Interest", "(City & State)");
    drawContentBox(rightX, topY + 7, colW, rightTopH);
    let mY = topY + 12;
    mY += drawBulletKV(rightX + 4, mY, colW - 8, "Home Market", formData.markets.home_market);
    if (formData.markets.additional_markets) {
      mY += drawBullet(rightX + 4, mY, colW - 8, `Additional Markets | ${formData.markets.additional_markets}`);
    }

    // Right bottom: Locations of Interest
    const locY = topY + 7 + rightTopH + 4;
    const locBoxH = H - locY - 10;
    drawBlackHeaderBar(rightX, locY, colW, "Locations of Interest", "(Full Addresses or Intersection)");
    drawContentBox(rightX, locY + 7, colW, locBoxH);
    if (formData.markets.first_store_location) {
      const lines = String(formData.markets.first_store_location).split("\n").filter((l) => l.trim());
      let lly = locY + 12;
      lines.forEach((l) => { lly += drawBullet(rightX + 4, lly, colW - 8, l); });
    }

    // ============================================================
    // SLIDE 4 — FINANCIAL PLAN
    // ============================================================
    pdf.addPage([W, H], "landscape");
    drawSlideChrome("Financial Plan", 4);

    const ti = formData.markets.total_investment;
    const totalInv =
      (parseFloat(ti.owner_equity) || 0) +
      (parseFloat(ti.debt_loans) || 0) +
      (parseFloat(ti.outside_investment) || 0) +
      (parseFloat(ti.working_capital) || 0) +
      (parseFloat(ti.reserve) || 0);

    const fundingBoxH = H - topY - 17;
    drawBlackHeaderBar(leftX, topY, colW, "Funding");
    drawContentBox(leftX, topY + 7, colW, fundingBoxH);
    let fY = topY + 13;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(...BLACK);
    pdf.text(`Total Investment Capital | ${money(totalInv)}`, leftX + 4, fY);
    fY += 7;
    const fundingRows = [
      ["Owner's Equity", money(ti.owner_equity)],
      ["Debt / Loans", money(ti.debt_loans)],
      ["Outside Investment", money(ti.outside_investment)],
      ["Working Capital", money(ti.working_capital)],
      ["Reserve", money(ti.reserve)],
    ];
    fundingRows.forEach(([k, v], i) => {
      // Alt row grey background
      if (i % 2 === 0) {
        pdf.setFillColor(...GREY_ROW);
        pdf.rect(leftX + 2, fY - 3.5, colW - 4, 5.5, "F");
      }
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...BLACK);
      pdf.text(k, leftX + 4, fY);
      pdf.text(v, leftX + colW - 4, fY, { align: "right" });
      fY += 5.5;
    });
    if (ti.notes) {
      fY += 3;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(...BLACK);
      pdf.text("Notes |", leftX + 4, fY);
      fY += 4;
      drawWrapped(ti.notes, leftX + 4, fY, colW - 8, { size: 8.5, lh: 3.6 });
    }

    // Right top: CapEx
    const capexH = 42;
    drawBlackHeaderBar(rightX, topY, colW, "Capital Expenditures", "(CapEx)");
    drawContentBox(rightX, topY + 7, colW, capexH);
    let cY = topY + 13;
    const cx = formData.financials.capex;
    const capexRows = [
      ["Store Buildout Budget", money(cx.buildout)],
      ["Equipment Budget", money(cx.equipment)],
      ["Inventory Budget", money(cx.inventory)],
      ["Grand Opening Budget", money(cx.grand_opening)],
      ["Contingency Fund", money(cx.contingency)],
    ];
    capexRows.forEach(([k, v], i) => {
      if (i % 2 === 0) {
        pdf.setFillColor(...GREY_ROW);
        pdf.rect(rightX + 2, cY - 3.5, colW - 4, 5.5, "F");
      }
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...BLACK);
      pdf.text(k, rightX + 4, cY);
      pdf.text(v, rightX + colW - 4, cY, { align: "right" });
      cY += 5.5;
    });

    // Right bottom: OpEx
    const opexY = topY + 7 + capexH + 4;
    const opexH = H - opexY - 10;
    drawBlackHeaderBar(rightX, opexY, colW, "Operating Expenses", "(OpEx)");
    drawContentBox(rightX, opexY + 7, colW, opexH);
    let oY = opexY + 13;
    const ox = formData.financials.opex;
    const opexRows = [
      ["Rent", money(ox.rent)],
      ["Insurance", money(ox.insurance)],
      ["Utilities", money(ox.utilities)],
      ["Software", money(ox.software)],
      ["Staffing / Payroll", money(ox.staffing_payroll)],
      ["Ongoing Marketing", money(ox.ongoing_marketing)],
      ["Additional Services / Overhead", money(ox.overhead)],
    ];
    opexRows.forEach(([k, v], i) => {
      if (i % 2 === 0) {
        pdf.setFillColor(...GREY_ROW);
        pdf.rect(rightX + 2, oY - 3.5, colW - 4, 5, "F");
      }
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...BLACK);
      pdf.text(k, rightX + 4, oY);
      pdf.text(v, rightX + colW - 4, oY, { align: "right" });
      oY += 5;
    });

    // ============================================================
    // SLIDE 5 — STORE SUPPORT PLAN (3 columns)
    // ============================================================
    pdf.addPage([W, H], "landscape");
    drawSlideChrome("Store Support Plan", 5);

    const col3W = (W - margin * 2 - 8) / 3;
    const c1x = margin;
    const c2x = margin + col3W + 4;
    const c3x = margin + (col3W + 4) * 2;
    const col3H = H - topY - 17;

    const drawSupportCol = (x, header, subHeader, blocks) => {
      drawBlackHeaderBar(x, topY, col3W, header, subHeader);
      drawContentBox(x, topY + 7, col3W, col3H);
      let sy = topY + 12;
      const anyContent = blocks.some(([, b]) => b);
      if (!anyContent) {
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(9);
        pdf.setTextColor(...GREY_MUTED);
        pdf.text("—", x + 4, sy);
        return;
      }
      blocks.forEach(([label, body]) => {
        if (!body) return;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9.5);
        pdf.setTextColor(...BLACK);
        pdf.text(label, x + 4, sy);
        sy += 4.5;
        sy += drawWrapped(body, x + 4, sy, col3W - 8, { size: 8.5, lh: 3.8 });
        sy += 3;
      });
    };

    drawSupportCol(c1x, "Staffing & Training", null, [
      ["Staffing Strategy", formData.support.staffing_strategy],
      ["Onboarding & Training", formData.support.onboarding_training],
    ]);
    const gom = formData.support.grand_opening_marketing;
    const om = formData.support.ongoing_marketing;
    const gomText = [gom.tactics, gom.budget ? `Budget: ${money(gom.budget)}` : ""].filter(Boolean).join("\n");
    const omText = [om.tactics, om.budget ? `Budget: ${money(om.budget)}` : ""].filter(Boolean).join("\n");
    drawSupportCol(c2x, "Marketing", null, [
      ["Grand Opening", gomText],
      ["Ongoing Marketing", omText],
    ]);
    drawSupportCol(c3x, "Compensation", "(Front-Line)", [
      ["Comp Plan", formData.support.frontline_comp_plan],
    ]);

    return pdf;
  };

  const handleDownload = () => {
    if (!formData.dealer_name.trim()) {
      toast.error("Please enter the Dealer Name (top of Business Overview)");
      return;
    }
    const pdf = generatePDF();
    pdf.save("Business_Plan_Org_Chart.pdf");
    toast.success("PDF downloaded");
  };

  const handleSave = async () => {
    if (!formData.dealer_name.trim()) {
      toast.error("Please enter the Dealer Name (top of Business Overview)");
      return;
    }
    setSaving(true);
    try {
      const pdf = generatePDF();
      const blob = pdf.output("blob");
      const file = new File([blob], "Business_Plan_Org_Chart.pdf", { type: "application/pdf" });
      await uploadMyDealerDocument(file, "business_plan_org_chart");
      toast.success("Business Plan & Org Chart saved to your documents");
      navigate("/dealer/documents");
    } catch {
      toast.error("Failed to save document");
    }
    setSaving(false);
  };

  // ------- UI per step -------
  const renderStep = () => {
    switch (currentStep) {
      case 0: {
        const o = formData.overview;
        return (
          <div className="space-y-5">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <Label className="text-xs font-medium text-slate-600">Dealer Name (for cover page)</Label>
              <Input
                value={formData.dealer_name}
                onChange={(e) => updateRoot("dealer_name", e.target.value)}
                placeholder="e.g. Prime Wireless of Detroit"
                className="text-sm mt-1"
                data-testid="bpoc-dealer-name"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">Business Name</Label>
                <Input value={o.business_name} onChange={(e) => updateSection("overview", "business_name", e.target.value)} className="text-sm" data-testid="bpoc-biz-name" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">DBA (if applicable)</Label>
                <Input value={o.dba} onChange={(e) => updateSection("overview", "dba", e.target.value)} className="text-sm" data-testid="bpoc-dba" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">HQ Location</Label>
                <Input value={o.hq_location} onChange={(e) => updateSection("overview", "hq_location", e.target.value)} placeholder="City, State" className="text-sm" data-testid="bpoc-hq" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Owner / Principal Name</Label>
                <Input value={o.owner_principal} onChange={(e) => updateSection("overview", "owner_principal", e.target.value)} className="text-sm" data-testid="bpoc-owner-principal" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-medium text-slate-600">Business Partners (if applicable)</Label>
                <Button size="sm" variant="outline" onClick={() => addListItem("overview", "partners", emptyPartner)} data-testid="bpoc-add-partner">
                  <Plus className="w-3.5 h-3.5 mr-1" />Add Partner
                </Button>
              </div>
              <div className="space-y-2">
                {o.partners.map((p) => (
                  <div key={p.id} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-4 text-sm" placeholder="Name" value={p.name} onChange={(e) => updateListItem("overview", "partners", p.id, "name", e.target.value)} />
                    <Input className="col-span-4 text-sm" placeholder="Role" value={p.role} onChange={(e) => updateListItem("overview", "partners", p.id, "role", e.target.value)} />
                    <select className="col-span-3 text-sm border border-slate-200 rounded px-2 py-2" value={p.status} onChange={(e) => updateListItem("overview", "partners", p.id, "status", e.target.value)}>
                      <option>Active</option>
                      <option>Silent</option>
                    </select>
                    <Button size="icon" variant="ghost" onClick={() => removeListItem("overview", "partners", p.id)} className="col-span-1 text-red-500"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium text-slate-600">Professional Experience / Background</Label>
              <Textarea rows={4} value={o.professional_experience} onChange={(e) => updateSection("overview", "professional_experience", e.target.value)} className="text-sm" data-testid="bpoc-experience" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-medium text-slate-600">Other Businesses Owned</Label>
                <Button size="sm" variant="outline" onClick={() => addListItem("overview", "other_businesses", emptyOtherBiz)} data-testid="bpoc-add-otherbiz">
                  <Plus className="w-3.5 h-3.5 mr-1" />Add Business
                </Button>
              </div>
              <div className="space-y-2">
                {o.other_businesses.map((b) => (
                  <div key={b.id} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-4 text-sm" placeholder="Business Name" value={b.name} onChange={(e) => updateListItem("overview", "other_businesses", b.id, "name", e.target.value)} />
                    <Input className="col-span-7 text-sm" placeholder="Description" value={b.description} onChange={(e) => updateListItem("overview", "other_businesses", b.id, "description", e.target.value)} />
                    <Button size="icon" variant="ghost" onClick={() => removeListItem("overview", "other_businesses", b.id)} className="col-span-1 text-red-500"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium text-slate-600">Previous Wireless Carriers</Label>
              <Textarea rows={3} value={o.previous_carriers} onChange={(e) => updateSection("overview", "previous_carriers", e.target.value)} placeholder="Which carriers have you sold, when, and for how long?" className="text-sm" data-testid="bpoc-prev-carriers" />
            </div>
          </div>
        );
      }
      case 1: {
        const org = formData.org;
        return (
          <div className="space-y-6">
            <div className="p-4 border border-slate-200 rounded-xl bg-slate-50">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-navy text-white flex items-center justify-center"><Briefcase className="w-4 h-4" /></div>
                <span className="text-sm font-medium text-navy">Owner / Principal</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input placeholder="Name" value={org.owner.name} onChange={(e) => updateNested("org", "owner", "name", e.target.value)} className="text-sm" data-testid="bpoc-org-owner-name" />
                <Input placeholder="Title" value={org.owner.title} onChange={(e) => updateNested("org", "owner", "title", e.target.value)} className="text-sm" />
                <Input placeholder="Phone" value={org.owner.phone} onChange={(e) => updateNested("org", "owner", "phone", e.target.value)} className="text-sm" />
                <Input placeholder="Email" value={org.owner.email} onChange={(e) => updateNested("org", "owner", "email", e.target.value)} className="text-sm" />
              </div>
            </div>

            <RoleBlock
              title="Sales"
              icon={<UserCircle className="w-4 h-4" />}
              iconBg="bg-teal"
              list={org.sales_team}
              onAdd={() => addListItem("org", "sales_team", emptyPerson)}
              onRemove={(id) => removeListItem("org", "sales_team", id)}
              onUpdate={(id, field, value) => updateListItem("org", "sales_team", id, field, value)}
              tidBase="bpoc-org-sales"
              helper="e.g. District Manager, Store Manager. Name is optional for regular reps."
            />
            <RoleBlock
              title="Operations"
              icon={<UserCircle className="w-4 h-4" />}
              iconBg="bg-indigo-500"
              list={org.operations_team}
              onAdd={() => addListItem("org", "operations_team", emptyPerson)}
              onRemove={(id) => removeListItem("org", "operations_team", id)}
              onUpdate={(id, field, value) => updateListItem("org", "operations_team", id, field, value)}
              tidBase="bpoc-org-ops"
              helper="e.g. Operations Manager, Inventory Lead, Technician."
            />
            <RoleBlock
              title="Other"
              icon={<Users className="w-4 h-4" />}
              iconBg="bg-slate-600"
              list={org.other_team}
              onAdd={() => addListItem("org", "other_team", emptyPerson)}
              onRemove={(id) => removeListItem("org", "other_team", id)}
              onUpdate={(id, field, value) => updateListItem("org", "other_team", id, field, value)}
              tidBase="bpoc-org-other"
              helper="e.g. HR, Marketing, Expansion Director, silent owners."
            />

            <div className="p-4 border border-slate-200 rounded-xl bg-slate-50">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center"><Users className="w-4 h-4" /></div>
                <span className="text-sm font-medium text-navy">Sales Associates</span>
              </div>
              <div className="flex items-center gap-3">
                <Button size="sm" variant="outline" onClick={() => updateSection("org", "sales_associate_count", Math.max(0, org.sales_associate_count - 1))}><Trash2 className="w-3.5 h-3.5" /></Button>
                <span className="text-2xl font-bold text-navy w-12 text-center" data-testid="bpoc-org-sa-count">{org.sales_associate_count}</span>
                <Button size="sm" variant="outline" onClick={() => updateSection("org", "sales_associate_count", org.sales_associate_count + 1)}><Plus className="w-3.5 h-3.5" /></Button>
                <span className="text-xs text-slate-500 ml-2">total sales associate {org.sales_associate_count === 1 ? "employee" : "employees"}</span>
              </div>
            </div>
          </div>
        );
      }
      case 2: {
        const m = formData.markets;
        return (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">Home Market</Label>
                <Input value={m.home_market} onChange={(e) => updateSection("markets", "home_market", e.target.value)} placeholder="e.g. Detroit, MI" className="text-sm" data-testid="bpoc-home-market" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">First Store Location</Label>
                <Input value={m.first_store_location} onChange={(e) => updateSection("markets", "first_store_location", e.target.value)} placeholder="Street address or intersection" className="text-sm" data-testid="bpoc-first-store" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Additional Markets (Ranked by Priority)</Label>
              <Textarea rows={3} value={m.additional_markets} onChange={(e) => updateSection("markets", "additional_markets", e.target.value)} className="text-sm" data-testid="bpoc-add-markets" />
            </div>

            <GrowthTable label="Store Growth Commitments — 0 to 6 Months" list={m.growth_0_6} onAdd={() => addListItem("markets", "growth_0_6", emptyMarket)} onRemove={(id) => removeListItem("markets", "growth_0_6", id)} onUpdate={(id, f, v) => updateListItem("markets", "growth_0_6", id, f, v)} tid="growth-0-6" />
            <GrowthTable label="6 to 12 Months" list={m.growth_6_12} onAdd={() => addListItem("markets", "growth_6_12", emptyMarket)} onRemove={(id) => removeListItem("markets", "growth_6_12", id)} onUpdate={(id, f, v) => updateListItem("markets", "growth_6_12", id, f, v)} tid="growth-6-12" />
            <GrowthTable label="1 to 2 Years" list={m.growth_1_2} onAdd={() => addListItem("markets", "growth_1_2", emptyMarket)} onRemove={(id) => removeListItem("markets", "growth_1_2", id)} onUpdate={(id, f, v) => updateListItem("markets", "growth_1_2", id, f, v)} tid="growth-1-2" />
            <GrowthTable label="2+ Years" list={m.growth_2plus} onAdd={() => addListItem("markets", "growth_2plus", emptyMarket)} onRemove={(id) => removeListItem("markets", "growth_2plus", id)} onUpdate={(id, f, v) => updateListItem("markets", "growth_2plus", id, f, v)} tid="growth-2plus" />

            <div>
              <Label className="text-xs font-medium text-slate-600">Growth Strategy</Label>
              <Textarea rows={4} value={m.growth_strategy} onChange={(e) => updateSection("markets", "growth_strategy", e.target.value)} className="text-sm" data-testid="bpoc-growth-strategy" />
            </div>

            <div className="p-4 border border-slate-200 rounded-xl bg-slate-50">
              <div className="text-sm font-medium text-navy mb-3">Total Investment Capital</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Money label="Owner's Equity" value={m.total_investment.owner_equity} onChange={(v) => updateNested("markets", "total_investment", "owner_equity", v)} tid="ti-owner-equity" />
                <Money label="Debt / Loans" value={m.total_investment.debt_loans} onChange={(v) => updateNested("markets", "total_investment", "debt_loans", v)} tid="ti-debt-loans" />
                <Money label="Outside Investment" value={m.total_investment.outside_investment} onChange={(v) => updateNested("markets", "total_investment", "outside_investment", v)} tid="ti-outside" />
                <Money label="Working Capital" value={m.total_investment.working_capital} onChange={(v) => updateNested("markets", "total_investment", "working_capital", v)} tid="ti-working" />
                <Money label="Reserve" value={m.total_investment.reserve} onChange={(v) => updateNested("markets", "total_investment", "reserve", v)} tid="ti-reserve" />
              </div>
              <div className="mt-3">
                <Label className="text-xs font-medium text-slate-600">Additional Notes</Label>
                <Textarea rows={2} value={m.total_investment.notes} onChange={(v) => updateNested("markets", "total_investment", "notes", v.target.value)} className="text-sm" />
              </div>
            </div>
          </div>
        );
      }
      case 3: {
        const f = formData.financials;
        return (
          <div className="space-y-5">
            <div className="p-4 border border-slate-200 rounded-xl">
              <div className="text-sm font-medium text-navy mb-3">Capital Expenditures (CapEx)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Money label="Store Buildout Budget" value={f.capex.buildout} onChange={(v) => updateNested("financials", "capex", "buildout", v)} tid="capex-buildout" />
                <Money label="Equipment Budget" value={f.capex.equipment} onChange={(v) => updateNested("financials", "capex", "equipment", v)} tid="capex-equipment" />
                <Money label="Inventory Budget" value={f.capex.inventory} onChange={(v) => updateNested("financials", "capex", "inventory", v)} tid="capex-inventory" />
                <Money label="Grand Opening Budget" value={f.capex.grand_opening} onChange={(v) => updateNested("financials", "capex", "grand_opening", v)} tid="capex-grand-opening" />
                <Money label="Contingency Fund" value={f.capex.contingency} onChange={(v) => updateNested("financials", "capex", "contingency", v)} tid="capex-contingency" />
              </div>
            </div>
            <div className="p-4 border border-slate-200 rounded-xl">
              <div className="text-sm font-medium text-navy mb-3">Operating Expenses (OpEx)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Money label="Rent" value={f.opex.rent} onChange={(v) => updateNested("financials", "opex", "rent", v)} tid="opex-rent" />
                <Money label="Insurance" value={f.opex.insurance} onChange={(v) => updateNested("financials", "opex", "insurance", v)} tid="opex-insurance" />
                <Money label="Utilities" value={f.opex.utilities} onChange={(v) => updateNested("financials", "opex", "utilities", v)} tid="opex-utilities" />
                <Money label="Software" value={f.opex.software} onChange={(v) => updateNested("financials", "opex", "software", v)} tid="opex-software" />
                <Money label="Staffing / Payroll" value={f.opex.staffing_payroll} onChange={(v) => updateNested("financials", "opex", "staffing_payroll", v)} tid="opex-payroll" />
                <Money label="Ongoing Marketing" value={f.opex.ongoing_marketing} onChange={(v) => updateNested("financials", "opex", "ongoing_marketing", v)} tid="opex-marketing" />
                <Money label="Additional Services / Overhead" value={f.opex.overhead} onChange={(v) => updateNested("financials", "opex", "overhead", v)} tid="opex-overhead" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Financial Plan Notes</Label>
              <Textarea rows={4} value={f.financial_plan_notes} onChange={(e) => updateSection("financials", "financial_plan_notes", e.target.value)} className="text-sm" placeholder="Explain how the numbers above fit together and how you'll fund the launch." data-testid="bpoc-fin-notes" />
            </div>
          </div>
        );
      }
      case 4: {
        const s = formData.support;
        return (
          <div className="space-y-5">
            <div>
              <Label className="text-xs font-medium text-slate-600">Staffing Strategy</Label>
              <Textarea rows={4} value={s.staffing_strategy} onChange={(e) => updateSection("support", "staffing_strategy", e.target.value)} className="text-sm" data-testid="bpoc-staffing" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Onboarding & Training Strategy</Label>
              <Textarea rows={4} value={s.onboarding_training} onChange={(e) => updateSection("support", "onboarding_training", e.target.value)} className="text-sm" data-testid="bpoc-training" />
            </div>
            <div className="p-4 border border-slate-200 rounded-xl bg-slate-50">
              <div className="text-sm font-medium text-navy mb-3">Grand Opening Marketing</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs font-medium text-slate-600">Tactics</Label>
                  <Textarea rows={2} value={s.grand_opening_marketing.tactics} onChange={(e) => updateNested("support", "grand_opening_marketing", "tactics", e.target.value)} className="text-sm" data-testid="bpoc-go-tactics" />
                </div>
                <Money label="Grand Opening Budget" value={s.grand_opening_marketing.budget} onChange={(v) => updateNested("support", "grand_opening_marketing", "budget", v)} tid="bpoc-go-budget" />
              </div>
            </div>
            <div className="p-4 border border-slate-200 rounded-xl bg-slate-50">
              <div className="text-sm font-medium text-navy mb-3">Ongoing Marketing</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs font-medium text-slate-600">Tactics</Label>
                  <Textarea rows={2} value={s.ongoing_marketing.tactics} onChange={(e) => updateNested("support", "ongoing_marketing", "tactics", e.target.value)} className="text-sm" data-testid="bpoc-om-tactics" />
                </div>
                <Money label="Ongoing Budget" value={s.ongoing_marketing.budget} onChange={(v) => updateNested("support", "ongoing_marketing", "budget", v)} tid="bpoc-om-budget" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Proposed Front-Line Compensation Plan</Label>
              <Textarea rows={5} value={s.frontline_comp_plan} onChange={(e) => updateSection("support", "frontline_comp_plan", e.target.value)} className="text-sm" placeholder="Base pay, commission structure, bonuses, targets, benefits..." data-testid="bpoc-comp-plan" />
            </div>
            <div className="p-4 bg-teal/5 border border-teal/20 rounded-xl">
              <div className="flex items-center gap-2 text-teal">
                <Check className="w-4 h-4" />
                <span className="text-sm font-medium">Ready to generate your combined Business Plan &amp; Org Chart PDF</span>
              </div>
              <p className="text-xs text-slate-500 mt-1 ml-6">Click &quot;Download PDF&quot; to preview, or &quot;Save to My Documents&quot; to upload it as your required Business Plan &amp; Organization Chart submission.</p>
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <DealerLayout>
      <div className="max-w-5xl mx-auto" data-testid="bpoc-builder-page">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dealer/documents")} data-testid="bpoc-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading font-bold text-2xl text-navy">Business Plan &amp; Organization Chart</h1>
            <p className="text-slate-500 font-body text-sm mt-0.5">Complete each section to generate your combined New Sub-Agent submission.</p>
          </div>
          <DraftStatusPill status={draftStatus} updatedAt={draftUpdatedAt} />
        </div>

        <StepIndicator currentStep={currentStep} setCurrentStep={setCurrentStep} />

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 bg-navy rounded-lg flex items-center justify-center text-white text-sm font-bold">
                {currentStep + 1}
              </div>
              <h2 className="font-heading font-semibold text-navy">{STEPS[currentStep].title}</h2>
            </div>
            <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2 }}>
              {renderStep()}
            </motion.div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between mt-6">
          <Button variant="outline" onClick={() => setCurrentStep((s) => s - 1)} disabled={currentStep === 0} data-testid="bpoc-prev-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <div className="flex gap-2">
            {currentStep === STEPS.length - 1 ? (
              <>
                <Button variant="outline" onClick={handleDownload} data-testid="bpoc-download-btn">
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-teal hover:bg-teal-dark text-white" data-testid="bpoc-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save to My Documents
                </Button>
              </>
            ) : (
              <Button onClick={() => setCurrentStep((s) => s + 1)} className="bg-navy hover:bg-navy/90 text-white" data-testid="bpoc-next-btn">
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </DealerLayout>
  );
}

function RoleBlock({ title, icon, iconBg, list, onAdd, onRemove, onUpdate, tidBase, helper }) {
  return (
    <div className="p-4 border border-slate-200 rounded-xl bg-slate-50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg text-white flex items-center justify-center ${iconBg}`}>{icon}</div>
          <span className="text-sm font-medium text-navy">{title}</span>
        </div>
        <Button size="sm" variant="outline" onClick={onAdd} data-testid={`${tidBase}-add`}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
      </div>
      {helper && <p className="text-xs text-slate-500 mb-2">{helper}</p>}
      <div className="space-y-2">
        {list.map((p) => (
          <div key={p.id} className="grid grid-cols-12 gap-2 items-center">
            <Input className="col-span-5 text-sm" placeholder="Name (optional)" value={p.name} onChange={(e) => onUpdate(p.id, "name", e.target.value)} />
            <Input className="col-span-6 text-sm" placeholder="Title" value={p.title} onChange={(e) => onUpdate(p.id, "title", e.target.value)} />
            <Button size="icon" variant="ghost" onClick={() => onRemove(p.id)} className="col-span-1 text-red-500"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function GrowthTable({ label, list, onAdd, onRemove, onUpdate, tid }) {
  return (
    <div className="p-4 border border-slate-200 rounded-xl">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-navy">{label}</div>
        <Button size="sm" variant="outline" onClick={onAdd} data-testid={`bpoc-${tid}-add`}><Plus className="w-3.5 h-3.5 mr-1" />Row</Button>
      </div>
      <div className="space-y-2">
        {list.map((m) => (
          <div key={m.id} className="grid grid-cols-12 gap-2 items-center">
            <Input className="col-span-6 text-sm" placeholder="Market (City, State)" value={m.market} onChange={(e) => onUpdate(m.id, "market", e.target.value)} />
            <Input className="col-span-2 text-sm" type="number" min="0" placeholder="# Stores" value={m.stores} onChange={(e) => onUpdate(m.id, "stores", e.target.value)} />
            <select className="col-span-3 text-sm border border-slate-200 rounded px-2 py-2" value={m.type} onChange={(e) => onUpdate(m.id, "type", e.target.value)}>
              <option>New</option>
              <option>Acquisition</option>
            </select>
            <Button size="icon" variant="ghost" onClick={() => onRemove(m.id)} className="col-span-1 text-red-500"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DraftStatusPill({ status, updatedAt }) {
  const relTime = (iso) => {
    if (!iso) return "";
    const diffSec = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (diffSec < 5) return "just now";
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
  };

  let content;
  let cls;
  if (status === "loading") {
    content = <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading draft…</>;
    cls = "bg-slate-100 text-slate-500";
  } else if (status === "saving") {
    content = <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>;
    cls = "bg-amber-100 text-amber-700";
  } else if (status === "saved") {
    content = <><Cloud className="w-3.5 h-3.5" /> Draft saved{updatedAt ? ` · ${relTime(updatedAt)}` : ""}</>;
    cls = "bg-teal/10 text-teal";
  } else if (status === "error") {
    content = <><CloudOff className="w-3.5 h-3.5" /> Autosave failed</>;
    cls = "bg-red-100 text-red-700";
  } else {
    content = <><Cloud className="w-3.5 h-3.5" /> Not saved yet</>;
    cls = "bg-slate-100 text-slate-500";
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}
      data-testid="bpoc-draft-status"
    >
      {content}
    </span>
  );
}

